import { Dropdown } from '@whistler-ui/k2/components/Dropdown';
import { InputField } from '@whistler-ui/k2/components/InputField';
import { Stack } from '@whistler-ui/k2/components/Stack';
import { cn } from '@whistler-ui/k2/helpers/classBuilders';
import { isFieldError } from '@whistler-ui/k2/helpers/formik';
import { capitalizeFirstLetter } from '@whistler-ui/k2/helpers/textHelpers';
import { formikInputLimit } from '@whistler-ui/portal/helpers/validation';
import { FormikProps } from 'formik';
import i18next from 'i18next';
import { ChangeEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DefaultField } from './components/DefaultField';
import { regexMap } from './helpers';

interface Parameter {
  name: string;
  type: string;
  required: boolean;
  multi_select?: boolean;
  options?: string[];
  belongs_to?: null;
  contains?: Parameter[];
}

interface FormProps {
  parameters: Parameter[];
  formik: FormikProps<any>;
}

const DynamicDataFields = ({ parameters, formik }: FormProps) => {
  const { t } = useTranslation();
  const { errors, handleBlur, touched, values, setFieldValue, handleChange } =
    formik;

  // Backend-provided option values are translated only via a dedicated,
  // namespaced key. Looking up the raw value against the global i18n key space
  // caused collisions (e.g. a device value of "default"/"other" resolving to
  // unrelated copy in the active locale). Fall back to a capitalized value.
  const getOptionLabel = (option: string) => {
    const optionKey = `componentTestDataPane_option_${option}`;

    if (i18next.exists(optionKey)) {
      return t(optionKey);
    }

    return capitalizeFirstLetter(option);
  };

  // Split params to render single first and nested at the end
  const [singleParams, containsParams] = useMemo(() => {
    return parameters.reduce<[typeof parameters, typeof parameters]>(
      ([single, contains], parameter) => {
        if (parameter.contains && parameter.contains.length > 0) {
          contains.push(parameter);
        } else {
          single.push(parameter);
        }
        return [single, contains];
      },
      [[], []],
    );
  }, [parameters]);

  const handleChangeOnInput = (
    e: ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: any) => void,
    regex: RegExp,
  ) => {
    handleChange(e);

    if (setFieldValue) {
      formikInputLimit(e as any, setFieldValue, regex);
    }
  };

  const renderField = (parameter: Parameter, parentName = '') => {
    const { name, required, options, contains, belongs_to, multi_select } =
      parameter;
    const fieldName = parentName ? `${parentName}--${name}` : name; // Append parent name if exists

    // If the field contains nested parameters, render a fieldset with nested fields
    if (contains && contains.length > 0) {
      return (
        <fieldset key={fieldName} className="min-h-10 mb-4">
          <legend className="py-4 text-xl font-bold">
            {t(`componentTestDataPane_${name}_parameters`)}
            <span
              className={cn({
                "after:content-['_*_'] after:text-red-500 after:text-sm after:font-bold":
                  required,
              })}
            />
          </legend>
          <Stack direction="column" gap={4}>
            {contains.map(nestedParam => renderField(nestedParam, fieldName))}
          </Stack>
        </fieldset>
      );
    }

    // If the field has options, render a select dropdown
    if (options && options.length > 0) {
      if (multi_select) {
        return (
          <div key={fieldName} className={'test-data-field'}>
            <InputField.Label id={fieldName} required={required}>
              {t(`componentTestDataPane_${name}_label`) || ''}
            </InputField.Label>
            <Dropdown.SelectCheckbox
              fullWidth
              label={t(`componentTestDataPane_${name}_label_select`) || ''}
              id={fieldName}
              onSubmit={(name, value) =>
                handleChange({ target: { name, value } })
              }
              options={options.map(option => ({
                label: getOptionLabel(option),
                id: option,
              }))}
              defaultSelectedOptions={values[fieldName]}
              withActions={false}
              selectAll={false}
            />
          </div>
        );
      }

      return (
        <div key={fieldName} className={'test-data-field'}>
          <Dropdown.Select
            variant="default"
            hasSearch={options.length > 1}
            label={t(`componentTestDataPane_${name}_label`) || ''}
            required={required}
            name={fieldName}
            value={values[fieldName] || ''}
            placeholder={t(`componentTestDataPane_device_select_a_${name}`)}
            onChange={handleChange}
            onBlur={() => handleBlur({ target: { name: fieldName } })}
            error={isFieldError(fieldName, touched, errors)}
            errorMessage={
              errors[fieldName] ? t(String(errors[fieldName])) : undefined
            }
            dataCy={`test-data-pane-dropdown-${name}`}
          >
            {options.map(option => (
              <Dropdown.Select.Option key={option} value={option}>
                {getOptionLabel(option)}
              </Dropdown.Select.Option>
            ))}
          </Dropdown.Select>
        </div>
      );
    }

    const {
      type: predefinedType,
      onChangeRegex,
      ...fieldProps
    } = getFieldProps(name);

    const defaultProps = {
      isRequired: Boolean(required),
      setFieldValue,
      handleBlur,
      values,
      errors,
      onChangeRegex,
      handleChangeOnInput,
      handleChange,
      touched,
      fieldProps,
      fieldName,
      name,
    };

    // If the field belongs to another field, render a fieldset with nested fields
    if (belongs_to) {
      return (
        <fieldset key={fieldName} className="min-h-10 mb-4">
          <legend className="py-4 text-xl font-bold">
            {t(`componentTestDataPane_${belongs_to as string}_parameters`)}{' '}
            <span
              className={cn({
                "after:content-['_*_'] after:text-red-500 after:text-sm after:font-bold":
                  required,
              })}
            />
          </legend>
          <DefaultField {...defaultProps} />
        </fieldset>
      );
    }

    // Otherwise, render a default field
    return <DefaultField key={fieldName} {...defaultProps} />;
  };

  return (
    <div className="flex flex-col gap-8 w-full">
      {singleParams.length > 0 && (
        <div className="grid grid-cols-2 gap-8">
          {singleParams.map(parameter => renderField(parameter))}
        </div>
      )}
      {containsParams.length > 0 && (
        <div className="grid grid-cols-2 gap-8 items-start">
          {containsParams.map(parameter => renderField(parameter))}
        </div>
      )}
    </div>
  );
};

/** HELPERS **/
regexMap.imei1 = regexMap.device_imei1;
regexMap.imei2 = regexMap.device_imei2;
regexMap.iccid = regexMap.esim_iccid;
regexMap.sim_iccid = regexMap.esim_iccid;
regexMap.eid = regexMap.device_eid;

const getFieldProps = (name: string) => regexMap[name]?.props || {};

export default DynamicDataFields;
